import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import {
	Typography,
	CircularProgress,
	Button,
	Divider,
	InputAdornment,
	TextField,
	IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { SettingsSecurity, useGetSecuritySettingsQuery, useUpdateSecuritySettingsMutation } from '../SettingsApi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

type FormType = SettingsSecurity;

const defaultValues: FormType = {
	password: '',
	password_confirmation: ''
};

/**
 * Form Validation Schema
 */
const schema = z
	.object({
		password: z.string().min(8, 'Password must be at least 8 characters long'),
		password_confirmation: z.string()
	})
	.refine((data) => data.password === data.password_confirmation, {
		message: 'Passwords must match',
		path: ['password_confirmation'] // Error will be shown on confirmPassword field
	});

type ErrorRecord = {
	name: keyof FormType;
	message: string;
};

function SecurityTab() {
	const navigate = useNavigate();
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const { data: securitySettings } = useGetSecuritySettingsQuery();
	const [updateSecuritySettings, { error: updateError, isSuccess, isLoading }] =
		useUpdateSecuritySettingsMutation();

	const { control, setError, reset, handleSubmit, formState } = useForm<FormType>({
		defaultValues,
		mode: 'all',
		resolver: zodResolver(schema)
	});

	const { isValid, dirtyFields, errors } = formState;

	useEffect(() => {
		reset(securitySettings);
	}, [securitySettings, reset]);

	useEffect(() => {
		reset({ ...securitySettings, password: '', password_confirmation: '' });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess]);

	useEffect(() => {
		if (updateError) {
			(updateError as any)?.map?.((err: ErrorRecord) => {
				setError(err.name, { type: 'manual', message: err.message });
				return undefined;
			});
		}
	}, [updateError, setError]);

	const signOut = async () => {
		try {
			await fetch('/api/logout', { method: 'GET' });
			navigate('/sign-in');
		} catch (error) {
			console.error('Logout failed', error);
		}
	};

	/**
	 * Form Submit
	 */
	async function onSubmit(formData: FormType) {
		try {
			const response: any = await updateSecuritySettings(formData);

			if (response?.error) {
				toast.error(response?.error?.data?.errors?.[0]?.title || 'Something went wrong!');
			} else {
				toast.success('Password Updated Successfully!');
				signOut();
			}
		} catch (err) {
			console.log(err);
		}
	}

	return (
		<div className="w-full max-w-5xl">
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="w-full">
					<Typography className="text-xl">Change your password</Typography>
				</div>
				<div className="mt-8 grid w-full gap-1.5 sm:grid-cols-4 space-y-8">
					<div className="sm:col-span-4">
						<Controller
							name="password"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									label="New Password"
									type={showNewPassword ? 'text' : 'password'}
									error={!!errors.password}
									helperText={errors?.password?.message}
									variant="outlined"
									fullWidth
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<FuseSvgIcon size={20}>heroicons-solid:key</FuseSvgIcon>
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
													onClick={() => setShowNewPassword((previous) => !previous)}
													onMouseDown={(event) => event.preventDefault()}
													edge="end"
												>
													{showNewPassword ? (
														<VisibilityOff sx={{ fontSize: 19 }} />
													) : (
														<Visibility sx={{ fontSize: 19 }} />
													)}
												</IconButton>
											</InputAdornment>
										)
									}}
								/>
							)}
						/>
					</div>
					<div className="sm:col-span-4">
						<Controller
							name="password_confirmation"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									label="Confirm password"
									type={showConfirmPassword ? 'text' : 'password'}
									error={!!errors.password_confirmation}
									variant="outlined"
									fullWidth
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<FuseSvgIcon size={20}>heroicons-solid:key</FuseSvgIcon>
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
													onClick={() => setShowConfirmPassword((previous) => !previous)}
													onMouseDown={(event) => event.preventDefault()}
													edge="end"
												>
													{showConfirmPassword ? (
														<VisibilityOff sx={{ fontSize: 19 }} />
													) : (
														<Visibility sx={{ fontSize: 19 }} />
													)}
												</IconButton>
											</InputAdornment>
										)
									}}
									helperText={errors?.password_confirmation?.message}
								/>
							)}
						/>
					</div>
				</div>

				<Divider className="mb-10 mt-11 border-t" />
				<div className="flex items-center justify-end space-x-2">
					<Button
						variant="outlined"
						disabled={_.isEmpty(dirtyFields) || isLoading}
						onClick={() => reset(securitySettings)}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="secondary"
						disabled={_.isEmpty(dirtyFields) || !isValid || isLoading}
						type="submit"
						startIcon={isLoading && <CircularProgress size={20} />}
					>
						Save
					</Button>
				</div>
			</form>
		</div>
	);
}

export default SecurityTab;
